import type { EntityCrudPageProps, FieldConfig } from '../../components/EntityCrudPage';

const STATE_FIELDS = [
  { key: 'state', label: 'State', type: 'text' as const, required: true, width: 0.5 as const, showInTable: true },
  { key: 'abbreviation', label: 'Abbr.', type: 'text' as const, required: true, width: 0.5 as const, showInTable: true },
];

/** Shown only once its governing checkbox is ticked. */
const dependsOn = (key: string) => (form: Record<string, unknown>) => Boolean(form[key]);

const FILING_WINDOW_FIELD: FieldConfig = {
  key: 'filingWindow',
  label: 'Filing Window',
  type: 'filing-window',
  showInTable: true,
  helperText:
    'When this filing can first be submitted, and how long the agency takes afterwards. ' +
    'The deadline rules above say when it is late; this says when the window opens.',
};

const FILING_STATUS_FIELD: FieldConfig = {
  key: 'filingStatusRules',
  label: 'Past Due / Revoked Status Rules',
  type: 'filing-status-rules',
  showInTable: true,
  helperText:
    'What changes once the entity is out of good standing with this agency — reinstatement ' +
    'forms, fees, penalties, and whether missed periods must also be filed.',
};

export const entityCompliance: EntityCrudPageProps = {
  title: 'Entity Compliance',
  resourcePath: '/admin/entity-compliance',
  getRowLabel: (r) => String(r.entityKey ?? r._id),
  fields: [
    { key: 'entityKey', label: 'Entity Key', type: 'text', required: true, width: 0.5, showInTable: true, helperText: 'e.g. 501c3' },
    { key: 'name', label: 'Name', type: 'text', width: 0.5, showInTable: true },
    { key: 'form990Required', label: 'Form 990 Required', type: 'bool', width: 0.5, showInTable: true },
    { key: 'unrelatedBusinessLimit', label: 'Unrelated Business Limit', type: 'bool', width: 0.5, showInTable: true },
    { key: 'politicalActivityProhibited', label: 'Political Activity Prohibited', type: 'bool', width: 0.5 },
    { key: 'privateBenefitProhibited', label: 'Private Benefit Prohibited', type: 'bool', width: 0.5 },
    { key: 'donorDisclosure', label: 'Donor Disclosure', type: 'bool', width: 0.5, showInTable: true },
    { key: 'publicCharityTest', label: 'Public Charity Test', type: 'bool', width: 0.5 },
    { key: 'specialNotes', label: 'Special Notes', type: 'multiline' },
  ],
};

export const stateCharitableSolicitation: EntityCrudPageProps = {
  title: 'State Charitable Solicitation',
  resourcePath: '/admin/state-charitable-solicitation',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'registrationRequired', label: 'Registration Required', type: 'bool', width: 0.5, showInTable: true },
    { key: 'agency', label: 'Agency', type: 'text', showInTable: true },
    { key: 'agencyDivision', label: 'Agency Division', type: 'text' },
    { key: 'formName', label: 'Form Name', type: 'text', width: 0.5 },
    {
      key: 'renewalPeriod',
      label: 'Renewal Period',
      type: 'select',
      width: 0.5,
      showInTable: true,
      options: [
        { value: 'Annual', label: 'Annual' },
        { value: 'Biennial', label: 'Biennial' },
        { value: 'N/A', label: 'N/A' },
      ],
    },
    { key: 'initialFee', label: 'Initial Fee', type: 'text', width: 0.5 },
    { key: 'renewalFee', label: 'Renewal Fee', type: 'text', width: 0.5 },
    { key: 'revenueThresholdForExemption', label: 'Revenue Threshold For Exemption', type: 'text' },

    { key: 'religiousExemption', label: 'Religious Org Exemption', type: 'bool', width: 0.5 },
    {
      key: 'religiousExemptionDetails',
      label: 'Religious Exemption — Requirements & Forms',
      type: 'multiline',
      visibleWhen: dependsOn('religiousExemption'),
    },
    { key: 'smallOrgExemption', label: 'Small Org Exemption', type: 'bool', width: 0.5 },
    {
      key: 'smallOrgThreshold',
      label: 'Small Org Threshold',
      type: 'text',
      width: 0.5,
      visibleWhen: dependsOn('smallOrgExemption'),
    },
    { key: 'educationalExemption', label: 'Educational Exemption', type: 'bool', width: 0.5 },
    {
      key: 'educationalExemptionDetails',
      label: 'Educational Exemption — Requirements & Forms',
      type: 'multiline',
      visibleWhen: dependsOn('educationalExemption'),
    },

    { key: 'acceptsURS', label: 'Accepts URS', type: 'bool', width: 0.5 },
    {
      key: 'doesNotAccept990N',
      label: 'Does Not Accept 990-N',
      type: 'bool',
      width: 0.5,
      showInTable: true,
      helperText: 'State rejects the 990-N as the 990 attachment on this filing.',
    },
    { key: 'onlineFilingAvailable', label: 'Online Filing Available', type: 'bool', width: 0.5 },
    { key: 'deadlineBasis', label: 'Deadline Basis (legacy text)', type: 'text', helperText: 'Free-form summary. Prefer using Deadline Rules below for new entries.' },
    {
      key: 'deadlineRules',
      label: 'Deadline Rules',
      type: 'deadline-rules',
      showInTable: true,
      helperText: 'Add one or more conditions joined by OR. The earliest applicable condition is the deadline.',
    },
    FILING_WINDOW_FIELD,
    FILING_STATUS_FIELD,
    {
      key: 'requiresCertificateOfAuthority',
      label: 'Requires Certificate of Authority',
      type: 'bool',
      width: 0.5,
      helperText: 'Detail lives in the Certificate of Authority reference set.',
    },
    { key: 'disclosureRequired', label: 'Disclosure Required', type: 'bool', width: 0.5 },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const stateAnnualReports: EntityCrudPageProps = {
  title: 'State Annual Reports',
  resourcePath: '/admin/state-annual-reports',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'required', label: 'Required', type: 'bool', width: 0.5, showInTable: true },
    { key: 'agency', label: 'Agency', type: 'text', showInTable: true },
    {
      key: 'frequency',
      label: 'Frequency',
      type: 'select',
      width: 0.5,
      showInTable: true,
      options: [
        { value: 'Annual', label: 'Annual' },
        { value: 'Biennial', label: 'Biennial' },
        { value: 'Decennial', label: 'Decennial' },
        { value: 'Quinquennial', label: 'Quinquennial' },
        { value: 'N/A', label: 'N/A' },
      ],
    },
    { key: 'fee', label: 'Fee', type: 'text', width: 0.5, showInTable: true },
    { key: 'deadline', label: 'Deadline (legacy text)', type: 'text', width: 0.5, helperText: 'Free-form summary. Prefer using Deadline Rules below for new entries.' },
    {
      key: 'deadlineRules',
      label: 'Deadline Rules',
      type: 'deadline-rules',
      showInTable: true,
      helperText: 'Add one or more conditions joined by OR. The earliest applicable condition is the deadline.',
    },
    FILING_WINDOW_FIELD,
    FILING_STATUS_FIELD,
    {
      key: 'requiresRegisteredAgent',
      label: 'Requires Registered Agent',
      type: 'bool',
      width: 0.5,
      showInTable: true,
    },
    { key: 'onlineFilingAvailable', label: 'Online Filing Available', type: 'bool', width: 0.5 },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

// ─── State tax exemptions, split three ways ───────────────────────────────

export const stateCorporateTaxExemption: EntityCrudPageProps = {
  title: 'State Corporate Tax Exemption',
  resourcePath: '/admin/state-corporate-tax-exemption',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'hasCorporateTaxExemption', label: 'Has Corporate Tax Exemption', type: 'bool', width: 0.5, showInTable: true },
    {
      key: 'separateExemptionApplicationRequired',
      label: 'Separate Exemption App Required',
      type: 'bool',
      width: 0.5,
      showInTable: true,
    },
    { key: 'agency', label: 'Agency', type: 'text', showInTable: true },
    { key: 'incomeTaxExemptionAgency', label: 'Income Tax Exemption Agency (legacy)', type: 'text' },
    { key: 'frequency', label: 'Frequency', type: 'text', width: 0.5 },
    { key: 'fee', label: 'Fee', type: 'text', width: 0.5 },
    { key: 'form', label: 'Form', type: 'text', width: 0.5 },
    {
      key: 'deadlineRules',
      label: 'Deadline Rules',
      type: 'deadline-rules',
      helperText: 'Add one or more conditions joined by OR. The earliest applicable condition is the deadline.',
    },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const propertyTaxExemption: EntityCrudPageProps = {
  title: 'Property Tax Exemption',
  resourcePath: '/admin/property-tax-exemption',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'hasPropertyTaxExemption', label: 'Has Property Tax Exemption', type: 'bool', width: 0.5, showInTable: true },
    { key: 'agency', label: 'Agency', type: 'text', showInTable: true },
    { key: 'frequency', label: 'Frequency', type: 'text', width: 0.5 },
    { key: 'fee', label: 'Fee', type: 'text', width: 0.5 },
    { key: 'form', label: 'Form', type: 'text', width: 0.5 },
    { key: 'exemptionProcess', label: 'Exemption Process', type: 'multiline' },
    {
      key: 'deadlineRules',
      label: 'Deadline Rules',
      type: 'deadline-rules',
      helperText: 'Add one or more conditions joined by OR. The earliest applicable condition is the deadline.',
    },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const salesTaxExemption: EntityCrudPageProps = {
  title: 'Sales Tax Exemption',
  resourcePath: '/admin/sales-tax-exemption',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'hasSalesTaxExemption', label: 'Has Sales Tax Exemption', type: 'bool', width: 0.5, showInTable: true },
    { key: 'agency', label: 'Agency', type: 'text', showInTable: true },
    { key: 'frequency', label: 'Frequency', type: 'text', width: 0.5 },
    { key: 'fee', label: 'Fee', type: 'text', width: 0.5 },
    { key: 'form', label: 'Form', type: 'text', width: 0.5 },
    { key: 'renewalRequired', label: 'Renewal Required', type: 'bool', width: 0.5 },
    { key: 'exemptionProcess', label: 'Exemption Process', type: 'multiline' },
    {
      key: 'deadlineRules',
      label: 'Deadline Rules',
      type: 'deadline-rules',
      helperText: 'Add one or more conditions joined by OR. The earliest applicable condition is the deadline.',
    },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

// ─── Fundraising, split from charitable gaming ────────────────────────────

export const stateProfessionalFundraiserRequirements: EntityCrudPageProps = {
  title: 'State Professional Fundraiser Requirements',
  resourcePath: '/admin/state-professional-fundraiser-requirements',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    {
      key: 'professionalFundraiserRegistrationRequired',
      label: 'Pro Fundraiser Reg. Required',
      type: 'bool',
      width: 0.5,
      showInTable: true,
    },
    {
      key: 'professionalSolicitorRegistrationRequired',
      label: 'Pro Solicitor Reg. Required',
      type: 'bool',
      width: 0.5,
      showInTable: true,
    },
    { key: 'suretyBondRequired', label: 'Surety Bond Required', type: 'bool', width: 0.5 },
    {
      key: 'suretyBondAmount',
      label: 'Surety Bond Amount',
      type: 'text',
      width: 0.5,
      visibleWhen: dependsOn('suretyBondRequired'),
    },
    { key: 'rafflesAllowed', label: 'Raffles Allowed', type: 'bool', width: 0.5 },
    { key: 'raffleLicenseRequired', label: 'Raffle License Required', type: 'bool', width: 0.5 },
    { key: 'raffleNotes', label: 'Raffle Notes', type: 'multiline' },
    {
      key: 'onlineSolicitationTriggers',
      label: 'Online Solicitation Triggers',
      type: 'bool',
      width: 0.5,
    },
    {
      key: 'charlestonPrinciplesAdopted',
      label: 'Charleston Principles Adopted',
      type: 'bool',
      width: 0.5,
    },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const stateCharitableGamingLicense: EntityCrudPageProps = {
  title: 'State Charitable Gaming License',
  resourcePath: '/admin/state-charitable-gaming-license',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    {
      key: 'charitableGamingLicenseRequired',
      label: 'Charitable Gaming License Required',
      type: 'bool',
      width: 0.5,
      showInTable: true,
    },
    { key: 'agency', label: 'Agency', type: 'text', showInTable: true },
    { key: 'formName', label: 'Form Name', type: 'text', width: 0.5 },
    { key: 'fee', label: 'Fee', type: 'text', width: 0.5 },
    { key: 'charitableGamingNotes', label: 'Charitable Gaming Notes', type: 'multiline', showInTable: true },
    {
      key: 'deadlineRules',
      label: 'Deadline Rules',
      type: 'deadline-rules',
      helperText: 'Add one or more conditions joined by OR. The earliest applicable condition is the deadline.',
    },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

// ─── New reference sets ───────────────────────────────────────────────────

export const certificateOfAuthority: EntityCrudPageProps = {
  title: 'Certificate of Authority',
  resourcePath: '/admin/certificate-of-authority',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'agency', label: 'Agency', type: 'text', showInTable: true },
    { key: 'agencyDivision', label: 'Agency Division', type: 'text' },
    { key: 'formName', label: 'Form Name', type: 'text', width: 0.5, showInTable: true },
    { key: 'fee', label: 'Fee', type: 'text', width: 0.5, showInTable: true },
    {
      key: 'filingRules',
      label: 'Filing Rules',
      type: 'deadline-rules',
      showInTable: true,
      helperText: 'Add one or more conditions joined by OR. The earliest applicable condition governs.',
    },
    FILING_WINDOW_FIELD,
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const auditThresholds: EntityCrudPageProps = {
  title: 'Audit Thresholds',
  resourcePath: '/admin/audit-thresholds',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    {
      key: 'tiers',
      label: 'Revenue Thresholds',
      type: 'audit-tiers',
      showInTable: true,
      helperText:
        'One entry per requirement — a state may set a different revenue threshold for each.',
    },
    {
      key: 'filedWithSolicitation',
      label: 'Filed With Solicitation Registration',
      type: 'bool',
      width: 0.5,
      showInTable: true,
    },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const npoStateIncorporation: EntityCrudPageProps = {
  title: 'NPO State Incorporation',
  resourcePath: '/admin/npo-state-incorporation',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'filingFee', label: 'AoI Filing Fee', type: 'text', width: 0.5, showInTable: true },
    { key: 'filingFeeNotes', label: 'Filing Fee Notes', type: 'text', width: 0.5 },
    { key: 'minimumBoardMembers', label: 'Min. Board Members', type: 'number', width: 0.5, showInTable: true },
    { key: 'minimumIncorporators', label: 'Min. Incorporators', type: 'number', width: 0.5, showInTable: true },
    { key: 'onlineFilingCompliant', label: 'Online Filing Compliant', type: 'bool', width: 0.5 },
    { key: 'onlineFilingOnly', label: 'Online Filing ONLY', type: 'bool', width: 0.5 },
    { key: 'nameAvailabilityRequirements', label: 'Name Availability / Reservation Requirements', type: 'multiline' },
    { key: 'submissionStyle', label: 'Production / Submission Style', type: 'text' },
    { key: 'attachmentRequired', label: 'Attachment Required', type: 'bool', width: 0.5 },
    {
      key: 'attachmentNotes',
      label: 'Attachment Notes',
      type: 'multiline',
      visibleWhen: dependsOn('attachmentRequired'),
    },
    { key: 'approvalTimeAndNotification', label: 'Approval Time / Notification Method', type: 'text' },
    { key: 'formsPortalUrl', label: 'Link to Forms / Filing Portal', type: 'text' },
    { key: 'registeredAgentSignatureRequired', label: 'Registered Agent Signature Required', type: 'bool', width: 0.5 },
    { key: 'eSignToFileAllowed', label: 'E-Sign to File Allowed', type: 'bool', width: 0.5 },
    { key: 'filingsAvailableToDownload', label: 'Filings Available to Download', type: 'bool', width: 0.5 },
    {
      key: 'filingsDownloadUrl',
      label: 'Filings Download URL',
      type: 'text',
      width: 0.5,
      visibleWhen: dependsOn('filingsAvailableToDownload'),
    },
    { key: 'treasurerRequired', label: 'Treasurer Required', type: 'bool', width: 0.5 },
    { key: 'soleMemberFilingsAllowed', label: 'Sole Member Filings Allowed', type: 'bool', width: 0.5 },
    { key: 'transferOfMembershipAllowed', label: 'Transfer of Membership Allowed', type: 'bool', width: 0.5 },
    { key: 'proxyVotingMembersAllowed', label: 'Proxy Voting — Members Allowed', type: 'bool', width: 0.5 },
    { key: 'proxyVotingDirectorsAllowed', label: 'Proxy Voting — Directors Allowed', type: 'bool', width: 0.5 },
    { key: 'conversionAllowed', label: 'Conversion Allowed', type: 'bool', width: 0.5 },
    { key: 'maximumTermLengths', label: 'Maximum Term Lengths', type: 'text', width: 0.5 },
    { key: 'tieVoteAllowed', label: 'Tie Vote Allowed', type: 'bool', width: 0.5 },
    { key: 'emailVotingAllowed', label: 'E-mail Voting Allowed', type: 'bool', width: 0.5 },
    { key: 'businessLicenseFilingRequired', label: 'Business License Filing Required', type: 'bool', width: 0.5 },
    { key: 'distributionsToMembersAllowed', label: 'Distributions to "Members" Allowed', type: 'bool', width: 0.5 },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const npoStateAmendment: EntityCrudPageProps = {
  title: 'NPO State Amendment',
  resourcePath: '/admin/npo-state-amendment',
  getRowLabel: (r) => String(r.state ?? r._id),
  fields: [
    ...STATE_FIELDS,
    { key: 'filingFee', label: 'AoA Filing Fee', type: 'text', width: 0.5, showInTable: true },
    { key: 'filingFeeNotes', label: 'Filing Fee Notes', type: 'text', width: 0.5 },
    {
      key: 'approvalMechanismRequired',
      label: 'Approval Mechanism Required',
      type: 'select',
      width: 0.5,
      showInTable: true,
      options: [
        { value: 'BOD Vote', label: 'BOD Vote' },
        { value: 'Member Vote', label: 'Member Vote' },
        { value: 'Both', label: 'Both' },
        { value: 'Varies', label: 'Varies' },
      ],
    },
    { key: 'signatoryRequirement', label: 'Signatory Requirement', type: 'text', width: 0.5 },
    { key: 'agencyMailingAddress', label: 'Mailing Address for State Agency', type: 'multiline' },
    { key: 'sosWebsiteUrl', label: 'SOS Website URL', type: 'text' },
    { key: 'registeredAgentRequirement', label: 'Registered Agent Requirement', type: 'text' },
    {
      key: 'amendmentFormDownloadAvailable',
      label: 'Amendment Form Available to Download',
      type: 'bool',
      width: 0.5,
    },
    {
      key: 'processingTime',
      label: 'Processing Time',
      type: 'json',
      helperText:
        'How long the agency takes once filed: { "minDays": 5, "maxDays": 10, "businessDays": true, "notes": "" }',
    },
    { key: 'submissionStyle', label: 'Production / Submission Style', type: 'text' },
    { key: 'attachmentRequired', label: 'Attachment Required', type: 'bool', width: 0.5 },
    {
      key: 'attachmentNotes',
      label: 'Attachment Notes',
      type: 'multiline',
      visibleWhen: dependsOn('attachmentRequired'),
    },
    { key: 'approvalTimeAndNotification', label: 'Approval Time / Notification Method', type: 'text' },
    { key: 'formsPortalUrl', label: 'Link to Forms / Filing Portal', type: 'text' },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const federalFilings: EntityCrudPageProps = {
  title: 'Federal Filings',
  resourcePath: '/admin/federal-filings',
  getRowLabel: (r) => String(r.formNumber ?? r._id),
  fields: [
    { key: 'formNumber', label: 'Form #', type: 'text', required: true, width: 0.5, showInTable: true },
    { key: 'name', label: 'Name', type: 'text', width: 0.5, showInTable: true },
    { key: 'description', label: 'Description', type: 'multiline' },
    { key: 'whoMustFile', label: 'Who Must File', type: 'multiline', showInTable: true },
    {
      key: 'appliesToFoundationStatus',
      label: 'Applies To Foundation Status',
      type: 'string-array',
      showInTable: true,
      helperText:
        'Comma-separated: publicCharity, privateFoundation. Leave blank to apply regardless — ' +
        'a private foundation files the 990-PF where a public charity files the 990/990-EZ/990-N.',
    },
    { key: 'threshold', label: 'Threshold', type: 'text', width: 0.5 },
    { key: 'deadline', label: 'Deadline (legacy text)', type: 'text', width: 0.5, helperText: 'Free-form. Prefer Deadline Rules below.' },
    {
      key: 'deadlineRules',
      label: 'Deadline Rules',
      type: 'deadline-rules',
      showInTable: true,
      helperText: 'Add one or more conditions joined by OR; the earliest applicable wins. Add per-rule conditions for thresholds (e.g. revenue ≥ $200k).',
    },
    FILING_WINDOW_FIELD,
    FILING_STATUS_FIELD,
    { key: 'extensionForm', label: 'Extension Form', type: 'text', width: 0.5 },
    { key: 'extensionDuration', label: 'Extension Duration', type: 'text', width: 0.5 },
    { key: 'penalty', label: 'Penalty', type: 'multiline' },
    {
      key: 'electronicFilingRequired',
      label: 'Electronic Filing Required',
      type: 'bool',
      width: 0.5,
      showInTable: true,
    },
    { key: 'frequency', label: 'Frequency', type: 'text', width: 0.5 },
    { key: 'notes', label: 'Notes', type: 'multiline' },
  ],
};

export const form990Schedules: EntityCrudPageProps = {
  title: 'Form 990 Schedules',
  resourcePath: '/admin/form-990-schedules',
  getRowLabel: (r) => `Schedule ${r.schedule ?? r._id}`,
  fields: [
    { key: 'schedule', label: 'Schedule', type: 'text', required: true, width: 0.5, showInTable: true },
    { key: 'name', label: 'Name', type: 'text', width: 0.5, showInTable: true },
    { key: 'description', label: 'Description', type: 'multiline' },
    { key: 'requiredWhen', label: 'Required When', type: 'multiline', showInTable: true },
    {
      key: 'partIVRules',
      label: 'Part IV Rules (per 990 type)',
      type: 'part-iv-rules',
      showInTable: true,
      helperText: 'Part IV triggers differ per 990 variant, so give each form its own entry.',
    },
    {
      key: 'partIVLine',
      label: 'Part IV Line (legacy)',
      type: 'text',
      width: 0.5,
      helperText: 'Superseded by Part IV Rules. Kept for reference.',
    },
    {
      key: 'applicableForms',
      label: 'Applicable Forms',
      type: 'string-array',
      width: 0.5,
      showInTable: true,
      helperText: 'Comma-separated, e.g. 990, 990-EZ',
    },
  ],
};

export const ENTITY_CONFIGS: Record<string, EntityCrudPageProps> = {
  'entity-compliance': entityCompliance,
  'state-charitable-solicitation': stateCharitableSolicitation,
  'state-annual-reports': stateAnnualReports,
  'certificate-of-authority': certificateOfAuthority,
  'state-corporate-tax-exemption': stateCorporateTaxExemption,
  'property-tax-exemption': propertyTaxExemption,
  'sales-tax-exemption': salesTaxExemption,
  'state-professional-fundraiser-requirements': stateProfessionalFundraiserRequirements,
  'state-charitable-gaming-license': stateCharitableGamingLicense,
  'audit-thresholds': auditThresholds,
  'npo-state-incorporation': npoStateIncorporation,
  'npo-state-amendment': npoStateAmendment,
  'federal-filings': federalFilings,
  'form-990-schedules': form990Schedules,
};
